import type { ApplicationDto, IncomeType, UtmMetricDto } from '@/api/backend/users/applications';
import type { ApiSchema } from '@/types/api';
import type { ExpenseTypes } from '@/api/backend/public';
import type {
  AdditionalFundsPurposeEnum,
  AdditionalIncomeValue,
  ApplicantAsset,
  ApplicantExpense,
  ApplicantIncome,
  ApplicantLiability,
  AppUser,
  AssetTypeEnum,
  Employment,
  ErhlDrawdown,
  Financials,
  LoanApplication,
  PropertyOwnershipType,
} from '@/types';
import { frequency } from '../helpers';
import {
  AdditionalFundsPurpose,
  ErhlEquityAccessMethod,
  LiabilityType,
  LoanType,
  PropertyType,
  SaleStatus,
} from '@/helpers/const';
import { formatDateTz, htmlToPlainText, stripHtml } from '@/helpers/format';
import type { AdditionalFundsRequiredProperty } from '@/types/AdditionalFundsRequiredProperty';
import type { IncomingProperty, PropertyTypeEnum } from '@/types/Property';
import { defaultAddress, defaultRetirementDetails } from '@/helpers/builder';
import type { Applicant } from '@/types/Applicant';
import { mapIdsTypeFromApi } from '../helpers';

export type AdditionalFundsRequiredPropertyDto = ApiSchema['AdditionalFundsRequiredProperty'];

// FY27 negative gearing fields are returned by the broker scenario retrieve endpoint but are not yet
// typed on the generated ApplicationDto property and asset schemas, so we cast until API types are updated.
type NegativeGearingFy27ApiFields = {
  isNewBuild?: boolean | null;
  isContractExchanged?: boolean | null;
};

const mapNegativeGearingFields = (source: NegativeGearingFy27ApiFields) => ({
  isNewBuild: source.isNewBuild ?? undefined,
  isContractExchanged: source.isContractExchanged ?? undefined,
});

/**
 * Whether the scenario never captured a comprehensive expense breakdown.
 *
 * Simple expenses are one household total. The scenario tools still persist a row for every
 * expense category, defaulted to $0, and those amounts remain after a switch back from
 * comprehensive to simple. The application collects the breakdown, so those rows must not
 * prefill as $0. Bridging and ERHL both start from this mapper.
 * @param application the scenario being converted
 */
const isScenarioExpensesSimple = (application: ApplicationDto): boolean => {
  const scenarioDetails = (application as { scenarioDetails?: { sectionStates?: { expenses?: string } } })
    .scenarioDetails;
  return scenarioDetails?.sectionStates?.expenses === 'simple';
};

// JS-10768 — the scenario retrieve endpoints return the stored addressManuallyCreated flag, but the
// customer property schemas do not type it yet. A scenario address that came from the address lookup
// must be prefilled as such: the DS "Address manually created" rule raises a Warning 1 on the
// loan for every property resubmitted with the flag set, and the prefill used to hardcode it to true.
type AddressManuallyCreatedApiField = {
  addressManuallyCreated?: boolean | null;
};

const prefilledAddressManuallyCreated = (source: AddressManuallyCreatedApiField | undefined): boolean =>
  source?.addressManuallyCreated ?? false;

export type FundsAssetFields = Pick<
  ApiSchema['FundsAssetCurrentVersion'],
  'assetType' | 'assetAmount' | 'assetDescription' | 'vehicleMake' | 'vehicleModel' | 'yearBought' | 'institution'
>;

/** Maps funds assets to the application's asset entries, leaving out the savings already contributed to a purchase. */
export const toApplicantAssets = (assets: FundsAssetFields[]): ApplicantAsset[] =>
  assets
    .map((asset, assetIndex) => {
      if (asset.assetType === 'motor_vehicles') {
        return {
          type: asset.assetType as AssetTypeEnum,
          make: asset.vehicleMake?.name ?? '',
          model: asset.vehicleModel?.name ?? '',
          yearBought: asset.yearBought?.toString() ?? '',
          value: asset.assetAmount ?? 0,
          assetNumber: assetIndex,
        };
      }
      if (asset.assetType === 'machinery' || asset.assetType === 'equipment') {
        return {
          type: asset.assetType as AssetTypeEnum,
          description: asset.assetDescription ?? '',
          value: asset.assetAmount ?? 0,
          assetNumber: assetIndex,
        };
      }
      return {
        type: asset.assetType as AssetTypeEnum,
        institution: asset.institution?.name ?? '',
        value: asset.assetAmount ?? 0,
        assetNumber: assetIndex,
      };
    })
    .filter(
      (item) =>
        !('institution' in item) ||
        (item.institution?.toLocaleLowerCase() ?? '').trim() !== 'savings to contribute to purchase',
    ) as ApplicantAsset[];

const mapApplicantFinancials = (application: ApplicationDto, expenseTypes: ExpenseTypes) => {
  const expensesSectionIsSimple = isScenarioExpensesSimple(application);
  let lastAddressIndex = 0;
  const getNextAddressIndex = () => {
    lastAddressIndex++;
    return lastAddressIndex;
  };
  let lastLiabilityIndex = 0;
  const getNextLiabilityIndex = () => {
    lastLiabilityIndex++;
    return lastLiabilityIndex;
  };
  const financials: Record<string, Financials> = {};
  const fundsAdditionalIncome = (incomeType: IncomeType, applicantId: string | undefined) => {
    return (application.fundsAdditionalIncome ?? [])
      .filter((item) => item.applicantId === applicantId)
      .find((item) => item.incomeType === incomeType);
  };
  // Carries the row id so an applicant update sends it back and the backend keeps the row across
  // versions (JS-10484). A type the applicant has no row for gets an empty, id-less entry.
  const additionalIncomeValue = (incomeType: IncomeType, applicantId: string | undefined): AdditionalIncomeValue => {
    const row = fundsAdditionalIncome(incomeType, applicantId);
    return {
      ...(row?.id ? { id: row.id } : {}),
      amount: row?.amount ?? 0,
      frequency: frequency[row?.frequency ?? 'monthly'],
      ...(typeof row?.isTaxable === 'boolean' ? { isTaxable: row.isTaxable } : {}),
    };
  };
  if (expenseTypes?.length) {
    expenseTypes.sort((a, b) => {
      const sortOrderA = a.sortOrder || 0;
      const sortOrderB = b.sortOrder || 0;
      return sortOrderA - sortOrderB;
    });
  }

  const getApplicantExpense = (applicantId: string | undefined) => {
    const expenseTypesMap: Record<
      string,
      { amount: number | null; frequency: string; expenseTypeId: string | undefined }
    > = {};
    // Simple mode never collected these categories. Skip the stored rows, including zeros and
    // any comprehensive values left behind after switching back to simple.
    if (expensesSectionIsSimple) {
      return expenseTypesMap;
    }
    for (const expenseType of expenseTypes) {
      if (!expenseType.type) {
        continue;
      }
      if (expenseTypesMap[expenseType.type]) {
        continue;
      }
      const fundExpense = (application.fundsExpenses ?? []).find(
        (item) => item.applicantId === applicantId && item.expenseTypeId === expenseType.id,
      );
      if (fundExpense?.expenseAmount === undefined || fundExpense?.expenseAmount === null) {
        continue;
      }
      // For ERHL private health insurance the backend adopts the higher of the declared and the
      // age benchmark (verified) value, so the resolved `expenseAmount` returns the benchmark when it
      // wins. The application must still pull through what the customer declared, so prefill from the
      // application value rather than the adopted value for this category.
      const isErhlPrivateHealth =
        application.lendingProduct === 'erhl' && expenseType.type === 'private_health_insurance';
      expenseTypesMap[expenseType.type] = {
        amount: (isErhlPrivateHealth ? fundExpense?.expenseAmountApplicationValue : fundExpense?.expenseAmount) ?? null,
        frequency: 'month',
        expenseTypeId: expenseType.id,
      };
    }
    return expenseTypesMap;
  };

  const getAssetPropertyShare = (propertyId: string | undefined) => {
    const share: Record<string, number> = {};
    for (const [applicantIndex, applicant] of application.applicants.entries()) {
      const propertiesOwnership = application.propertiesOwnership.find(
        (item) => item.applicantId === applicant.id && item.assetPropertyId === propertyId,
      );
      share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
    }
    return share;
  };

  const getExpenseExpectation = (applicantId: string | undefined) => {
    return application.expenseExpectations.find((item) => item.applicantId === applicantId);
  };

  for (const [applicantIndex, applicant] of application.applicants.entries()) {
    const employmentDetail = (application.employmentDetails ?? []).find((item) => item.applicantId === applicant.id);
    const incomeExpectation = application.incomeExpectations.find((item) => item.applicantId === applicant.id);
    const applicantExpense = getApplicantExpense(applicant.id);
    const expenseExpectation = getExpenseExpectation(applicant.id);
    let expenses: ApplicantExpense | null = null;
    if (expensesSectionIsSimple) {
      // Dependants are captured in simple mode. Category amounts are not, and a
      // change-in-expenses answer from comprehensive mode is not carried over.
      expenses = {
        numberOfDependants: applicant.numberOfDependants ?? 0,
        predictedExpenseChange: {
          amount: 0,
          frequency: frequency.monthly,
        },
        expenseChangeReason: '',
        changesInLivingExpense: 'no_change',
      };
    } else if (
      Object.keys(applicantExpense).length ||
      expenseExpectation?.expenseAmount ||
      expenseExpectation?.changesInLivingExpenses
    ) {
      expenses = {
        ...applicantExpense,
        numberOfDependants: applicant.numberOfDependants ?? 0,
        predictedExpenseChange: {
          amount: expenseExpectation?.expenseAmount ?? 0,
          frequency: frequency[expenseExpectation?.frequency ?? 'monthly'],
        },
        expenseChangeReason: expenseExpectation?.expenseChangeReason ?? '',
        changesInLivingExpense: expenseExpectation?.changesInLivingExpenses ?? 'no_change',
      };
    }

    const selfEmployedForApplicant = (application.fundsSelfEmployed ?? []).filter(
      (f) => f.applicantId === applicant.id,
    );
    const salariedFundsForApplicant = (application.fundsSalaried ?? []).filter(
      (fund) => fund.applicantId === applicant.id,
    );
    const additionalIncomeForApplicant = (application.fundsAdditionalIncome ?? []).filter(
      (item) => item.applicantId === applicant.id,
    );

    let income: ApplicantIncome | null = null;
    if (
      employmentDetail?.id ||
      incomeExpectation?.id ||
      salariedFundsForApplicant.length > 0 ||
      selfEmployedForApplicant.length > 0 ||
      additionalIncomeForApplicant.length > 0
    ) {
      const isPlaceholderSalaried = (employment: (typeof salariedFundsForApplicant)[number]): boolean => {
        const salary = employment.salaryAmount ?? 0;
        const employer = (employment.nameOfEmployer ?? '').toString().trim();
        return employment.jobType === 'full_time' && salary === 0 && employer.length === 0;
      };
      const salariedForApplicant =
        selfEmployedForApplicant.length > 0
          ? salariedFundsForApplicant.filter((e) => !isPlaceholderSalaried(e))
          : salariedFundsForApplicant;

      income = {
        applicantId: '',
        employment: [
          ...salariedForApplicant.map((employment, employmentIndex) => {
            const addressIndex = getNextAddressIndex();
            return {
              type: employment.jobType ?? 'full_time',
              salary: {
                amount: employment.salaryAmount ?? 0,
                frequency: frequency[employment.salaryFrequency ?? 'monthly'],
              },
              commission: {
                amount: employment.commissionsAmount ?? 0,
                frequency: frequency[employment.commissionsFrequency ?? 'monthly'],
              },
              bonuses: {
                amount: employment.bonusesAmount ?? 0,
                frequency: frequency[employment.bonusesFrequency ?? 'monthly'],
              },
              overtime: {
                amount: employment.overtimeAmount ?? 0,
                frequency: frequency[employment.overtimeFrequency ?? 'monthly'],
              },
              salarySacrifice: employment.isSalarySacrifice,
              employerName: employment.nameOfEmployer,
              employerAddress: {
                id: `applicant-${applicantIndex}-employment-${employmentIndex}-employer-address-option-${addressIndex}`,
                index: addressIndex,
                addressLine: employment.employerLine1 ?? '',
                locality: employment.employerCity ?? '',
                state: employment.employerState?.toLocaleUpperCase() ?? '',
                postcode: employment.employerPostCode ?? '',
                countryCode: 'AU',
                addressManuallyCreated: true,
              },
              employedSince: employment.employedSince ?? undefined,
              occupation: (employment as { occupation?: string }).occupation ?? '',
              industry: (employment as { industry?: string }).industry ?? '',
            };
          }),
          ...selfEmployedForApplicant.map((employment, employmentIndex) => {
            const addressIndex = getNextAddressIndex();
            return {
              type: 'self_employed',
              salary: {
                amount: employment.salaryAmount ?? 0,
                frequency: frequency[employment.salaryFrequency ?? 'monthly'],
              },
              businessAddress: {
                id: `applicant-${applicantIndex}-employment-${employmentIndex}-employer-address-option-${addressIndex}`,
                index: addressIndex,
                addressLine: employment.line1 ?? '',
                locality: employment.city ?? '',
                state: employment.state?.toLocaleUpperCase() ?? '',
                postcode: employment.postCode ?? '',
                countryCode: 'AU',
                addressManuallyCreated: true,
              },
              businessType: employment.businessType,
              tradingName: employment.tradingName,
              occupation: employment.occupation,
              industry: employment.industry,
              gstRegistered: employment.isGSTRegistered,
              abn: employment.australianBusinessNumber,
              soleTrader: employment.isSoleTrader,
              hasTaxReturns: employment.hasTaxReturnsLastTwoFinancialYears,
              employedSince: employment.employedSince ?? undefined,
              providedDocument: employment.providedDocument ?? '',
              otherProvidedDocument:
                employment.providedDocument === 'other' ? employment.otherProvidedDocument ?? '' : undefined,
            };
          }),
        ] as Employment[],
        shortTermChange: employmentDetail?.employmentChangeInShortTerm ?? false,
        shortTermEmploymentChange: stripHtml(employmentDetail?.detailsOnChange ?? ''),
        centrelinkPayments: additionalIncomeValue('centrelink_payments', applicant.id),
        childSupportMaintenance: additionalIncomeValue('child_support_maintenance', applicant.id),
        familyAllowance: additionalIncomeValue('family_allowance', applicant.id),
        familyTaxBenefit: additionalIncomeValue('family_tax_benefits', applicant.id),
        governmentPension: additionalIncomeValue('government_pension', applicant.id),
        dividendsInvestments: additionalIncomeValue('dividends', applicant.id),
        superannuation: additionalIncomeValue('superannuation', applicant.id),
        other: additionalIncomeValue('other', applicant.id),
        expectIncomeChange: incomeExpectation?.expectedChangesInNext12Months ?? false,
        effectOnIncomeChange: stripHtml(incomeExpectation?.financialPosition ?? ''),
        incomeExpectation: {},
        employmentDetail: {},
      };
    }

    financials[`applicant-${applicantIndex}`] = {
      income,
      retirementDetails: incomeExpectation?.id
        ? {
            ...defaultRetirementDetails(),
            financialChangesDuringLoan: incomeExpectation.expectedChangesInNext12Months ?? undefined,
            financialChangesDuringLoanDescription: stripHtml(incomeExpectation.financialPosition ?? ''),
          }
        : undefined,
      expenses,
      assets: [
        ...toApplicantAssets(
          (application.fundsAssets ?? []).filter((item) => item.applicantId === applicant.id && item.assetType),
        ),
        ...(application.assetsProperties ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((asset, assetIndex) => {
            const addressIndex = getNextAddressIndex();
            return {
              type: 'property',
              address: {
                id: `applicant-${applicantIndex}-asset-${assetIndex}-property-address-option-${addressIndex}`,
                index: addressIndex,
                addressLine: asset.line1 ?? '',
                locality: asset.city ?? '',
                state: asset.state?.toUpperCase() ?? '',
                postcode: asset.postCode ?? '',
                countryCode: 'AU',
                addressManuallyCreated: true,
              },
              propertyValue: asset.propertyValue ?? 0,
              propertyPurpose: asset.propertyPurpose,
              rentalIncome: {
                amount: asset.rentalIncome ?? 0,
                frequency: frequency[asset.rentalIncomeFrequency ?? 'monthly'],
              },
              amountOwing: asset.amountOwed ?? 0,
              assetNumber: assetIndex,
              propertyOwnership: {
                id: `asset-${assetIndex}`,
                type: 'asset',
                share: getAssetPropertyShare(asset.id),
              },
              ...mapNegativeGearingFields(asset as NegativeGearingFy27ApiFields),
            };
          }),
      ] as ApplicantAsset[],
      liabilities: [
        ...(application.liabilitiesCards ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((card) => {
            return {
              type: LiabilityType.CARD,
              institution: card.institution?.name ?? '',
              creditLimit: card.creditLimit ?? 0,
              amountOwing: card.amountOwed ?? 0,
              liabilityNumber: getNextLiabilityIndex() - 1,
            };
          }),
        ...(application.liabilitiesVehicles ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((vehicle) => {
            return {
              type: LiabilityType.VEHICLE,
              make: vehicle.vehicleMake?.name ?? '',
              model: vehicle.vehicleModel?.name ?? '',
              yearBought: vehicle.yearBought ?? '',
              assetValue: vehicle.assetValue ?? 0,
              liabilityLimit: vehicle.liabilityLimit ?? 0,
              amountOwing: vehicle.amountOwed,
              periodicRepayment: {
                amount: vehicle.monthlyRepayment ?? 0,
                frequency: 'month',
              },
              liabilityNumber: getNextLiabilityIndex() - 1,
            };
          }),
        ...(application.liabilitiesOthers ?? [])
          .filter((item) => item.applicantId === applicant.id)
          .map((other) => {
            return {
              type: LiabilityType.OTHER,
              details: other.details ?? '',
              liabilityLimit: other.liabilityLimit ?? 0,
              amountOwing: other.amountOwed ?? 0,
              periodicRepayment: {
                amount: other.monthlyRepayment ?? 0,
                frequency: 'month',
              },
              liabilityNumber: getNextLiabilityIndex() - 1,
            };
          })
          .filter((item) => item.details && item.amountOwing),
      ] as ApplicantLiability[],
    };
  }
  return financials;
};

export const buildErhlDrawdowns = (application: ApplicationDto): ErhlDrawdown[] | undefined => {
  if (application.lendingProduct !== 'erhl') return undefined;

  const drawdowns: ErhlDrawdown[] = [];

  const scenarioLumpSum = application.erhlDetails?.drawdowns?.find((d) => d.drawdownType === 'lump_sum');
  // Only treat the scenario drawdown as authoritative when it carries a positive amount; a zero/negative
  // amount must not shadow a valid additionalFundsRequired fallback (nullish `??` would otherwise keep the 0).
  // The date resolves separately from the amount: the drawdown's fundsRequiredBy is a virtual field that can
  // come back null on an otherwise valid positive-amount drawdown, so it must still fall back to
  // additionalFundsRequired instead of leaving "Funds required by" blank.
  const hasScenarioAmount = (scenarioLumpSum?.amount ?? 0) > 0;

  const lumpSumAmount = hasScenarioAmount
    ? scenarioLumpSum?.amount
    : application.additionalFundsRequired?.fundsRequiredAmount;
  const lumpSumFundsRequiredBy =
    (hasScenarioAmount ? scenarioLumpSum?.fundsRequiredBy : undefined) ??
    application.additionalFundsRequired?.fundsRequiredDate;

  if (lumpSumAmount && lumpSumAmount > 0) {
    drawdowns.push({
      drawdownType: 'lump_sum',
      amount: lumpSumAmount,
      // Preserve the scenario drawdown's effectiveFrom so LoanDetailsStep can round-trip it on submit.
      effectiveFrom: hasScenarioAmount ? scenarioLumpSum?.effectiveFrom ?? undefined : undefined,
      fundsRequiredBy: lumpSumFundsRequiredBy ?? undefined,
    });
  }

  // Carry the scenario's scheduled_equity_release drawdown through (amount + duration + frequency
  // + effectiveFrom) so the Loan Details step prefills the monthly drawdown fields from the scenario
  // instead of falling back to its hard-coded defaults.
  const scenarioScheduled = application.erhlDetails?.drawdowns?.find(
    (d) => d.drawdownType === 'scheduled_equity_release',
  );
  if ((scenarioScheduled?.amount ?? 0) > 0) {
    drawdowns.push({
      drawdownType: 'scheduled_equity_release',
      amount: scenarioScheduled?.amount,
      // Retrieve DTO reports duration in whole months; LoanDetailsStep converts to years for display.
      durationTotalMonths: scenarioScheduled?.durationTotalMonths ?? undefined,
      effectiveFrom: scenarioScheduled?.effectiveFrom ?? undefined,
      paymentFrequency: scenarioScheduled?.paymentFrequency ?? 'monthly',
    });
  }

  return drawdowns.length > 0 ? drawdowns : undefined;
};

/**
 * Whether the scenario's applicant dates of birth are placeholders rather than captured values.
 *
 * Simple applicant details never ask for a date of birth: the scenario tools store one derived from
 * the over-55 answer or the ERHL borrower age so calcs have an age. The CRM still shows the full
 * fields when the scenario's contact is the applicant. The scenario-start payload is a
 * BrokerScenarioDto cast to ApplicationDto, so `scenarioDetails` is read through a narrow view.
 * @param application the scenario being converted
 */
const isScenarioDateOfBirthPlaceholder = (application: ApplicationDto): boolean => {
  const scenarioDetails = (
    application as { scenarioDetails?: { sectionStates?: { applicantDetails?: string }; contactType?: string } }
  ).scenarioDetails;
  return scenarioDetails?.sectionStates?.applicantDetails === 'simple' && scenarioDetails.contactType !== 'applicant';
};

export const applicationProgress = (
  application: ApplicationDto,
  expenseTypes: ExpenseTypes,
  userType: AppUser,
): LoanApplication => {
  let lastAddressIndex = 0;
  const getNextAddressIndex = () => {
    lastAddressIndex++;
    return lastAddressIndex;
  };

  const loanType = () => {
    if (
      application.incomingProperties.find(
        (item) =>
          item.propertyType === PropertyType.VACANT_LAND ||
          item.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME,
      )
    ) {
      return LoanType.SINGLE_SECURITY;
    }
    if (application.loanType === 'buy_now') {
      return LoanType.BRIDGING;
    } else if (application.loanType === 'equity_release') {
      return LoanType.EQUITY_RELEASE;
    } else if (application.loanType === 'single_security') {
      return LoanType.SINGLE_SECURITY;
    }
    return undefined;
  };

  // making sure primary applicant is at index 0
  const primaryApplicant = application.applicants.find((item) => item.applicantType === 'primary');
  if (primaryApplicant) {
    application.applicants = [
      primaryApplicant,
      ...application.applicants.filter((applicant) => applicant.applicantType !== 'primary'),
    ];
  }

  const { additionalFundsRequired } = application;

  let additionalFundsRequiredProperties: AdditionalFundsRequiredProperty | undefined = undefined;
  let incomingProperties: IncomingProperty[] = application.incomingProperties.map((property, index) => {
    const addressIndex = getNextAddressIndex();
    return {
      id: `incoming-property-${index}`,
      settlementDate: application?.settlementDate ?? '',
      propertyType: property.propertyType ?? PropertyType.RESIDENTIAL,
      idsType: mapIdsTypeFromApi(property.idsType).idsType,
      commercialType: mapIdsTypeFromApi(property.idsType).commercialType,
      propertyLeased: property.isPropertyLeased,
      address: {
        id: `incoming-property-${index}-incoming-property-address-option-${addressIndex}`,
        index: addressIndex,
        addressLine: property.line1 ?? '',
        locality: property.city ?? '',
        state: property.state?.toLocaleUpperCase() ?? '',
        postcode: property.postCode ?? '',
        countryCode: 'AU',
        addressManuallyCreated: prefilledAddressManuallyCreated(property as AddressManuallyCreatedApiField),
      },
      propertyPurpose: property.propertyPurpose,
      contractForSale: property.contractForSale,
      purchaseAmount: property.purchasePrice,
      depositAmount: property.depositAmount,
      depositType: property.depositPaidType,
      stampDutyStatus: property.stampDutyStatus,
      stampDuty: property.stampDuty,
      securityHeldStatus: property.securityHeld?.status ?? 'security_not_held',
      receiveRentalIncome: property.rentalIncomeIsTaxable,
      rentalIncome: {
        amount: property.rentalIncome ?? 0,
        frequency: frequency[property.rentalIncomeFrequency ?? 'monthly'],
      },
      transferFeeCovered: property.transferFeeCovered,
      ...mapNegativeGearingFields(property as NegativeGearingFy27ApiFields),
      ...(index === 0 && application.lendingProduct === 'erhl'
        ? {
            erhlSavingsAmount: application.calculatedValue?.savingsToContributeToPurchase,
            erhlGiftedAmount: application.additionalFunds?.giftedFundsAmount,
          }
        : {}),
    };
  });
  if (loanType() === LoanType.SINGLE_SECURITY) {
    const property = application.additionalFundsRequiredProperties as AdditionalFundsRequiredPropertyDto;
    const addressIndex = getNextAddressIndex();
    const singleSecurityPropertyAddress = {
      id: `single-security-address-option-${addressIndex}`,
      index: addressIndex,
      addressLine: property?.line1 ?? '',
      locality: property?.city ?? '',
      state: property?.state?.toLocaleUpperCase() ?? '',
      postcode: property?.postCode ?? '',
      countryCode: 'AU',
      addressManuallyCreated: prefilledAddressManuallyCreated(property as AddressManuallyCreatedApiField | undefined),
    };

    let reasonForFundsOption: AdditionalFundsPurposeEnum = AdditionalFundsPurpose.OTHER;
    let propertyType: PropertyTypeEnum | null = incomingProperties[0]?.propertyType ?? null;
    let addressRequired = false;
    const loanPurpose = additionalFundsRequired.additionalFundsPurpose?.title?.toLocaleLowerCase() ?? '';
    if (loanPurpose === 'purchase retirement, land lease or community home') {
      reasonForFundsOption = AdditionalFundsPurpose.PURCHASE_RETIREMENT_LAND_LEASE_OR_COMMUNITY_HOME;
      propertyType = PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME;
      addressRequired = true;
    } else if (loanPurpose === 'cosmetic improvements before selling') {
      reasonForFundsOption = AdditionalFundsPurpose.COSMETIC_IMPROVEMENTS_BEFORE_SELLING;
    } else if (loanPurpose === 'purchase vacant land') {
      reasonForFundsOption = AdditionalFundsPurpose.PURCHASE_VACANT_LAND;
      propertyType = PropertyType.VACANT_LAND;
      addressRequired = true;
    }

    const fundsToContributeAmount = application.calculatedValue?.savingsToContributeToPurchase ?? 0;

    additionalFundsRequiredProperties = {
      isThereContractForSale: property?.contractForSale ?? false,
      isAdditionalFundsRequired: additionalFundsRequired.id !== undefined && additionalFundsRequired.id !== null,
      reasonForFundsOption,
      propertyType: property?.propertyType ?? undefined,
      propertyPurpose: property?.propertyPurpose ?? undefined,
      ...singleSecurityPropertyAddress,
      propertyAddress: addressRequired ? singleSecurityPropertyAddress : undefined,
      contributingFundsTowardsPurchase: fundsToContributeAmount !== 0,
      fundsToContributeAmount,
      settlementDate: additionalFundsRequired.fundsRequiredDate ?? '',
      purchasePrice: property?.purchasePrice ?? 0,
      depositAmount: property?.depositAmount ?? 0,
      depositType: property?.depositPaidType ?? undefined,
      stampDutyStatus: property?.stampDutyStatus ?? undefined,
      transferFeeCovered: property?.transferFeeCovered ?? false,
      description: stripHtml(additionalFundsRequired?.description ?? ''),
      fundsRequiredDate: additionalFundsRequired.fundsRequiredDate,
      fundsRequiredAmount: additionalFundsRequired.fundsRequiredAmount,
      additionalFundsPurposeId: additionalFundsRequired.additionalFundsPurposeId,
    } as AdditionalFundsRequiredProperty;
    if (incomingProperties.length === 0) {
      incomingProperties = [
        {
          id: `incoming-property-0`,
          settlementDate: additionalFundsRequired.fundsRequiredDate ?? '',
          propertyType: propertyType === null ? PropertyType.RESIDENTIAL : propertyType,
          address: propertyType === null ? defaultAddress() : singleSecurityPropertyAddress,
          contractForSale: propertyType === null ? true : property.contractForSale,
          purchaseAmount: additionalFundsRequired.fundsRequiredAmount ?? 0,
          depositAmount: propertyType === null ? 0 : property.depositAmount,
          depositType: propertyType === null ? undefined : property.depositPaidType,
          stampDutyStatus: propertyType === null ? undefined : property.stampDutyStatus,
          receiveRentalIncome: false,
          securityHeldStatus: 'security_not_held',
          transferFeeCovered: propertyType === null ? undefined : property.transferFeeCovered,
        },
      ];
    }
  }

  const erhlDrawdowns = buildErhlDrawdowns(application);

  return {
    additionalFundsRequired: {
      description: stripHtml(additionalFundsRequired?.description ?? ''),
      fundsRequiredAmount: additionalFundsRequired?.fundsRequiredAmount ?? 0,
      additionalFundsPurposeId: additionalFundsRequired?.additionalFundsPurposeId ?? '',
      fundsRequiredDate: additionalFundsRequired?.fundsRequiredDate ?? '',
      isAdditionalFundsRequired: (additionalFundsRequired?.additionalFundsPurposeId ?? '').toString().length > 0,
    },
    structuralVersionNumber: 2,
    applicationId: 'application',
    actionType: 'createNew',
    userType: userType,
    additionalFundsRequiredProperties,
    incomingProperties,
    outgoingProperties: application.outgoingProperties.map((property, index) => {
      const addressIndex = getNextAddressIndex();
      return {
        id: `outgoing-property-${index}`,
        propertyType: property.propertyType ?? PropertyType.RESIDENTIAL,
        address: {
          id: `outgoing-property-${index}-outgoing-property-address-option-${addressIndex}`,
          index: addressIndex,
          addressLine: property.line1 ?? '',
          locality: property.city ?? '',
          state: property.state?.toLocaleUpperCase() ?? '',
          postcode: property.postCode ?? '',
          countryCode: 'AU',
          addressManuallyCreated: prefilledAddressManuallyCreated(property as AddressManuallyCreatedApiField),
        },
        propertyPurpose: property.propertyPurpose ?? 'investment_property',
        outstandingLoanAmount: property.outstandingMortgageAmount ?? 0,
        saleStatus: property.soldStatus ?? SaleStatus.NONE,
        settlementDate: property.settlementDate ?? '',
        saleAmount: property.estimatedValuation ?? 0,
        depositAmount: property.depositAmountReceived ?? 0,
        depositInTrust: property.depositHeldInTrust ?? false,
        securityHeldStatus: property.securityHeld?.status ?? 'security_not_held',
        idsType: mapIdsTypeFromApi(property.idsType).idsType,
        commercialType: mapIdsTypeFromApi(property.idsType).commercialType,
        propertyLeased: property.isPropertyLeased,
      };
    }),
    supportingProperties: (application.supportingProperties ?? []).map((property, index) => {
      const addressIndex = getNextAddressIndex();
      return {
        id: `supporting-property-${index}`,
        propertyType: property.propertyType ?? PropertyType.RESIDENTIAL,
        address: {
          id: `supporting-property-${index}-supporting-property-address-option-${addressIndex}`,
          index: addressIndex,
          addressLine: property.line1 ?? '',
          locality: property.city ?? '',
          state: property.state?.toLocaleUpperCase() ?? '',
          postcode: property.postCode ?? '',
          countryCode: 'AU',
          addressManuallyCreated: prefilledAddressManuallyCreated(property as AddressManuallyCreatedApiField),
        },
        propertyPurpose: property.propertyPurpose ?? 'owner_occupied',
        outstandingLoanAmount: property.outstandingMortgageAmount ?? 0,
        supportingPropertyValue: property.estimatedValuation ?? 0,
        idsType: mapIdsTypeFromApi(property.idsType).idsType,
        commercialType: mapIdsTypeFromApi(property.idsType).commercialType ?? '',
        propertyLeased: property.isPropertyLeased,
        receiveRentalIncome: property.rentalIncomeIsTaxable,
        rentalIncome: {
          amount: property.rentalIncome ?? 0,
          frequency: frequency[property.rentalIncomeFrequency ?? 'monthly'],
        },
        securityHeldStatus: property.securityHeld?.status ?? 'security_not_held',
        ...mapNegativeGearingFields(property as NegativeGearingFy27ApiFields),
      };
    }),
    applicants: application.applicants.map((applicant, index) => {
      const addressIndex = getNextAddressIndex();
      const dateOfBirth = isScenarioDateOfBirthPlaceholder(application) ? undefined : applicant.dateOfBirth;
      let relationshipToPrimary = null;
      const applicantRelationship = (applicant.applicantRelationship ?? []).find(
        (item) => item.applicantId === applicant.id,
      );
      const relatedApplicantIndex: number = applicantRelationship
        ? application.applicants.findIndex((item) => item.id === applicantRelationship.relatedApplicantId)
        : -1;
      let relationshipOtherInfo = applicantRelationship?.otherRelationshipDescription ?? '';

      if (applicant.applicantType !== 'primary') {
        relationshipOtherInfo = applicantRelationship?.otherRelationshipDescription ?? '';
        relationshipToPrimary = applicantRelationship?.relationship;
      }

      return {
        id: `applicant-${index}`,
        type: applicant.applicantType ?? 'secondary',
        firstName: applicant.firstName ?? '',
        middleName: applicant.middleName ?? '',
        lastName: applicant.lastName ?? '',
        preferredName: applicant.preferredName ?? applicant.firstName ?? '',
        gender: applicant.gender ?? 'prefer_not_to_say',
        genderOther: '',
        birthdate: dateOfBirth ? formatDateTz(dateOfBirth) : '',
        address: {
          id: `applicant-${index}-address-option-${addressIndex}`,
          index: addressIndex,
          addressLine: applicant.line1 ?? '',
          locality: applicant.city ?? '',
          state: applicant.state?.toLocaleUpperCase() ?? '',
          postcode: applicant.postCode ?? '',
          countryCode: 'AU',
          addressManuallyCreated: true,
        },
        mobile: applicant.mobilePhoneNumber ?? '',
        email: applicant.email ?? '',
        confirmEmail: applicant.email ?? '',
        numberOfDependants: applicant.numberOfDependants ?? 0,
        relationshipStatus: applicant.relationshipStatus ?? 'single',
        relationshipOtherInfo,
        relationshipToPrimary: relationshipToPrimary ?? undefined,
        relatedApplicantIndex,
      } as unknown as Applicant;
    }),
    propertiesOwnership: [
      ...application.incomingProperties.map((property, propertyIndex) => {
        const share: Record<string, number> = {};
        for (const [applicantIndex, applicant] of application.applicants.entries()) {
          const propertiesOwnership = application.propertiesOwnership.find(
            (item) => item.applicantId === applicant.id && item.incomingPropertyId === property.id,
          );
          share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
        }
        return {
          id: `incoming-property-${propertyIndex}`,
          type: 'incoming' as PropertyOwnershipType,
          share,
        };
      }),
      ...application.outgoingProperties.map((property, propertyIndex) => {
        const share: Record<string, number> = {};
        for (const [applicantIndex, applicant] of application.applicants.entries()) {
          const propertiesOwnership = application.propertiesOwnership.find(
            (item) => item.applicantId === applicant.id && item.outgoingPropertyId === property.id,
          );
          share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
        }
        return {
          id: `outgoing-property-${propertyIndex}`,
          type: 'outgoing' as PropertyOwnershipType,
          share,
        };
      }),
      ...(application.supportingProperties ?? []).map((property, propertyIndex) => {
        const share: Record<string, number> = {};
        for (const [applicantIndex, applicant] of application.applicants.entries()) {
          const propertiesOwnership = (
            application.propertiesOwnership as ((typeof application.propertiesOwnership)[number] & {
              supportingPropertyId?: string;
            })[]
          ).find((item) => item.applicantId === applicant.id && item.supportingPropertyId === property.id);
          share[`applicant-${applicantIndex}`] = propertiesOwnership?.share ?? 0;
        }
        return {
          id: `supporting-property-${propertyIndex}`,
          type: 'supporting' as PropertyOwnershipType,
          share,
        };
      }),
    ],
    financials: mapApplicantFinancials(application, expenseTypes),
    endDebt: (application.calculatedValue?.endDebt ?? 0) * -1,
    netSurplus: (application.calculatedValue as { netSurplus?: number } | undefined)?.netSurplus ?? 0,
    verificationCode: '',
    brokerId: application.brokerId,
    utmMetrics: {} as UtmMetricDto,
    allowNoContractOfSale: false,
    brokerReferenceNumber: '',
    brokerFullName: '',
    brokerEmail: '',
    loanType: loanType(),
    settlementDate: application.settlementDate,
    loanDetails: {
      savingsAmount: application.calculatedValue?.savingsToContributeToPurchase ?? 0,
      giftedAmount: application.additionalFunds?.giftedFundsAmount ?? 0,
      reasonDescription: '',
      brokerFeePercentage: 0,
      borrowAmount: 0,
      calcLoanAmount: application.calculatedValue?.loanAmount ?? 0,
    },
    // An ERHL scenario's manually selected term lives on erhlDetails as a UsedValue trio and is
    // NOT mirrored to calculatedValue.loanTermMonths (that column goes stale on a manual
    // override). Resolve the manual value here so a scenario conversion carries it into the
    // submit body — there is no term input in the wizard, it just rides through (JS-9373).
    loanTermMonths:
      application.lendingProduct === 'erhl' &&
      application.erhlDetails?.loanTermMonthsUsed === 'manual_value' &&
      application.erhlDetails.loanTermMonthsManualValue != null
        ? application.erhlDetails.loanTermMonthsManualValue
        : application.calculatedValue?.loanTermMonths,
    productType: application.productType,
    lendingProduct: application.lendingProduct,
    erhlLoanPurposes: application.erhlDetails?.loanPurposes,
    erhlLoanPurposeOtherDescription: application.erhlDetails?.loanPurposeOtherDescription ?? '',
    // ERHL exit strategy + note-backed free text — prefill the wizard when starting an
    // application from a scenario. Dropdown value + advice come from erhlDetails (resolve the
    // active Used source); the free-text exit-strategy details and the additional-loan-information
    // summary notes are persisted as application notes, not columns.
    //
    // The scenario-start payload is a BrokerScenarioDto cast to ApplicationDto (see
    // useScenarioApi), so these scenario-only fields — erhlDetails' Used-value triple +
    // advice, and the notes array — aren't modelled on ApplicationDto. Read them
    // through a narrow scenario-shaped view.
    ...(() => {
      const erhl = application.erhlDetails as
        | {
            exitStrategyUsed?: string;
            exitStrategyManualValue?: string;
            exitStrategyApplicationValue?: string;
          }
        | undefined;
      const notes = (application as { notes?: { noteSubType?: string; body?: string }[] }).notes;
      return {
        exitStrategy: (erhl?.exitStrategyUsed === 'manual_value'
          ? erhl?.exitStrategyManualValue
          : erhl?.exitStrategyApplicationValue) as LoanApplication['exitStrategy'],
        exitStrategyDetails: notes?.find((note) => note.noteSubType === 'application_exit_strategy_details')?.body,
        // The scenario additional-info editor is rich text; the application field is a plain textarea.
        summaryNotes: htmlToPlainText(
          notes?.find((note) => note.noteSubType === 'application_summary_notes')?.body ?? '',
        ),
      };
    })(),
    erhlDrawdowns,
    // Derive the (required) equity access method from the scenario's scheduled equity release so the
    // Loan Details step reveals — and prefills — the monthly drawdown section. A positive monthly
    // scheduled amount ⇒ lump sum + scheduled release; otherwise lump sum only. Without this the
    // section stays hidden and the scenario's monthly amount is never shown.
    erhlEquityAccessMethod:
      application.lendingProduct === 'erhl'
        ? (erhlDrawdowns ?? []).some(
            (d) => d.drawdownType === 'scheduled_equity_release' && (Number(d.amount) || 0) > 0,
          )
          ? ErhlEquityAccessMethod.LUMP_SUM_AND_MONTHLY_DRAWDOWN
          : ErhlEquityAccessMethod.LUMP_SUM_ONLY
        : undefined,
  };
};
